// Canonical hash navigation for the intranet.
//
// The intranet uses a hash path as the single source of truth so that every
// navigation level has its own history entry. That keeps the browser (and PWA)
// back action going up one level instead of jumping to an unrelated module.
//
// Examples:
//   #faturamento
//   #faturamento/glosas
//   #faturamento/glosas/bradesco-saude
//   #faturamento/glosas/bradesco-saude/2026-06-01
//   #helpdesk?chamado_id=123

export const DEFAULT_INTRANET_MODULE = 'inicio';

export const INTRANET_MODULE_KEYS = new Set([
  'perfil', 'aniversariantes', 'helpdesk', 'tarefas', 'projetos',
  'capacitacao', 'agenda', 'diretorio', 'chat', 'enquetes',
  'faturamento', 'softwares',
]);

function decodeSegment(segment) {
  try {
    return decodeURIComponent(segment);
  } catch {
    return segment;
  }
}

export function parseIntranetHash(hash) {
  const raw = String(hash ?? '').replace(/^#/, '');
  const [pathPart = '', query = ''] = raw.split('?');
  const [candidate, ...segments] = pathPart
    .split('/')
    .filter(Boolean)
    .map(decodeSegment);
  const module = INTRANET_MODULE_KEYS.has(candidate) ? candidate : DEFAULT_INTRANET_MODULE;
  const ticketId = new URLSearchParams(query).get('chamado_id');

  return {
    module,
    segments,
    ticketId: module === 'helpdesk' ? ticketId : null,
  };
}

export function buildIntranetHash({ module = DEFAULT_INTRANET_MODULE, segments = [], ticketId = null } = {}) {
  const safeModule = INTRANET_MODULE_KEYS.has(module) ? module : DEFAULT_INTRANET_MODULE;
  const cleanSegments = segments.filter(Boolean).map(segment => encodeURIComponent(segment));
  const query = safeModule === 'helpdesk' && ticketId ? `?chamado_id=${encodeURIComponent(ticketId)}` : '';

  if (safeModule === DEFAULT_INTRANET_MODULE && !cleanSegments.length && !query) return '';

  return `#${[safeModule, ...cleanSegments].join('/')}${query}`;
}

export function intranetRouteKey({ module = DEFAULT_INTRANET_MODULE, segments = [] } = {}) {
  return [module, ...segments.filter(Boolean)].join('/');
}

// Full URL (path + search + hash) for a route, ready for history.pushState.
export function intranetRouteUrl(route) {
  return `${window.location.pathname}${window.location.search}${buildIntranetHash(route)}`;
}

// Navigates through the History API. Pushing (the default) gives every level
// its own entry; replacing is for corrections such as an unauthorized module
// falling back to a permitted one.
export function applyIntranetRoute(route, { replace = false } = {}) {
  const url = intranetRouteUrl(route);
  if (replace) window.history.replaceState(window.history.state, '', url);
  else window.history.pushState(window.history.state, '', url);
}
